import { FetchPolicy } from "@apollo/client";
import { useApolloClient, useMutation } from "@apollo/client/react";
import { format } from "date-fns";
import { useEffect, useState } from "react";

import { TrackerNS, UserNS } from "@smart/bridge-types-basic";
import { loadLocale } from "@smart/itops-locale-dom";
import { extractId } from "@smart/itops-types-basic";
import {
  downloadAsCSV,
  isNotNullOrUndefined,
  sortField,
  toSubdomain,
  v4,
} from "@smart/itops-utils-basic";
import {
  Gql,
  mutationDocuments,
  queryDocuments,
} from "@smart/manage-gql-operations-dom";

import { useIncrementalQueryFactory, useQueryFactory } from "./base";
import { generateCSVData } from "./submission-helpers";
import { optimisticOperation, updateAfterDelete } from "../cache";

export const useCreateSubmission = () =>
  useMutation(mutationDocuments.createSubmission, {
    update: (cache, { data }, { variables }) => {
      if (!data?.createSubmission || !variables) return;

      if (variables?.matterId) {
        const variableSets = [
          { matterId: variables.matterId },
          {
            matterId: variables.matterId,
            status: "active",
          },
        ] as const;
        variableSets.forEach((set) => {
          cache.updateQuery(
            {
              query: queryDocuments.matterSubmissions,
              variables: set,
            },
            (ex) => ({
              matterSubmissions: [
                data.createSubmission,
                ...(ex?.matterSubmissions || []),
              ],
              __typename: "Query" as const,
            }),
          );
        });
      }

      cache.updateQuery(
        {
          query: queryDocuments.submissions,
        },
        (ex) => ({
          submissions: [data.createSubmission, ...(ex?.submissions || [])],
          __typename: "Query" as const,
        }),
      );

      cache.updateQuery(
        {
          query: queryDocuments.formSubmissions,
          variables: {
            formUri: variables.formUri,
          },
        },
        (ex) => ({
          formSubmissions: [
            data.createSubmission,
            ...(ex?.formSubmissions || []),
          ],
          __typename: "Query" as const,
        }),
      );
    },
  });

export const useLoadAllSubmissions = useIncrementalQueryFactory(
  queryDocuments.paginatedSubmissions,
  {
    map: ({ paginatedSubmissions }) =>
      paginatedSubmissions.data.filter((s) => !s.values.deleted),
  },
);

export const useLoadSubmissions = useQueryFactory(queryDocuments.submissions, {
  map: ({ submissions }) =>
    sortField(
      submissions.filter((s) => !s.values.deleted),
      { key: (f) => f.values.updatedAt, dir: "desc" },
    ),
});

export const mapToMatterSubmission = (
  submission: Gql.SubmissionFieldsFragment,
) => ({
  uri: submission.uri,
  label: submission.values.label,
  formTitle: submission.form.values.title,
  formUri: submission.form.uri,
  createdAt: submission.values.createdAt,
  updatedAt: submission.values.updatedAt,
  questions: submission.form.fields,
  responses: submission.responses,
  formFields: submission.form.fields,
  status: submission.values.status,
  syncStatus: submission.values.syncStatus,
  recipient: submission.values.recipient,
  trackers: submission.trackers,
  rawSubmission: submission,
  sentAt: (submission.trackers && submission.trackers[0]?.sentAt) || undefined,
});

export const useLoadMatterSubmissions = useQueryFactory(
  queryDocuments.matterSubmissions,
  {
    map: ({ matterSubmissions }) =>
      matterSubmissions
        .filter((s) => !s.values.deleted)
        .map(mapToMatterSubmission),
  },
);

export type MatterSubmission = NonNullable<
  ReturnType<typeof useLoadMatterSubmissions>["result"]
>[number] & {
  externalSubmissionLink?: string;
  externalSubmissionEmbedUrl?: string;
  externalSubmissionType?: string;
};

export const useLoadFormSubmissions = useQueryFactory(
  queryDocuments.formSubmissions,
  {
    map: ({ formSubmissions }) =>
      sortField(
        formSubmissions.filter((s) => !s.values.deleted),
        { key: (f) => f.values.updatedAt, dir: "desc" },
      ),
  },
);

export const useInFlightSubmissionCount = useQueryFactory(
  queryDocuments.inFlightSubmissionCount,
  { map: ({ inFlightSubmissionCount }) => inFlightSubmissionCount },
);

export const useLoadScheduledReminderCount = () => {
  const apollo = useApolloClient();

  return () =>
    apollo
      .query({
        query: queryDocuments.scheduledReminderCount,
        fetchPolicy: "network-only",
      })
      .then((result) => result.data?.scheduledReminderCount ?? 0);
};

export const useQuerySubmission = (fetchPolicy?: FetchPolicy) => {
  const apollo = useApolloClient();

  return (variables: Gql.SubmissionQueryVariables) =>
    apollo
      .query({ query: queryDocuments.submission, variables, fetchPolicy })
      .then((result) => result.data?.submission);
};

export const useLoadSubmission = useQueryFactory(queryDocuments.submission, {
  map: ({ submission }) => submission,
});

export const useLoadSubmissionSummaries = useIncrementalQueryFactory(
  queryDocuments.submissionSummaries,
  {
    map: ({ submissionSummaries }) =>
      submissionSummaries.data.filter((s) => !s.values.deleted),
  },
);

export const useLoadWebEnquiries = (
  timeRange?: { from?: Date; to?: Date },
  options?: { reloadOnVisible?: boolean; pollInterval?: number },
) =>
  useLoadSubmissionSummaries(
    {
      status: "completed",
      communicationMethods: ["embed"],
      createdAtRange:
        timeRange?.from && timeRange.to
          ? {
              from: timeRange.from.toISOString(),
              to: timeRange.to.toISOString(),
            }
          : undefined,
    },
    options,
  );

/**
 * There is no time filter for pending submissions because the widget will have to fetch all pending submissions.
 * The Pending Forms list will filter by time at client-side.
 */
export const useLoadPendingSubmissions = (options?: {
  reloadOnVisible?: boolean;
  pollInterval?: number;
}) => {
  const [combinedResult, setCombinedResult] = useState<
    Gql.SubmissionSummaryFieldsFragment[]
  >([]);
  const {
    loading: activeLoading,
    result: activeSubmissions,
    refetch: refetchActives,
    refetching: activeRefetching,
    error: activeError,
  } = useLoadSubmissionSummaries(
    {
      status: "active",
      communicationMethods: [
        "communicate",
        "document",
        "email",
        "internalUse",
        null,
      ],
    },
    options,
  );

  const {
    loading: draftLoading,
    result: draftSubmissions,
    refetch: refetchDrafts,
    refetching: draftRefetching,
    error: draftError,
  } = useLoadSubmissionSummaries(
    {
      status: "draft",
      communicationMethods: [
        "communicate",
        "document",
        "email",
        "internalUse",
        null,
      ],
    },
    options,
  );

  useEffect(() => {
    if (!activeLoading && !draftLoading) {
      setCombinedResult([...activeSubmissions, ...draftSubmissions]);
    }
  }, [activeSubmissions, draftSubmissions, activeLoading, draftLoading]);

  return {
    loading: activeLoading || draftLoading,
    result: combinedResult.sort((a, b) => {
      const left = a.values.updatedAt;
      const right = b.values.updatedAt;
      if (left > right) return -1;
      if (right > left) return 1;
      return 0;
    }),
    refetching: activeRefetching || draftRefetching,
    refetch: () => {
      refetchActives();
      refetchDrafts();
    },
    error: activeError || draftError,
  };
};

export const useLoadCompletedSubmissions = (
  timeRange?: { from?: Date; to?: Date },
  options?: { reloadOnVisible?: boolean; pollInterval?: number },
) =>
  useLoadSubmissionSummaries(
    {
      status: "completed",
      communicationMethods: [
        "communicate",
        "document",
        "email",
        "internalUse",
        null,
      ],
      completedAtRange:
        timeRange?.from && timeRange.to
          ? {
              from: timeRange.from.toISOString(),
              to: timeRange.to.toISOString(),
            }
          : undefined,
    },
    options,
  );

export const submissionLink = (
  sub: Gql.SubmissionFieldsFragment | Gql.SubmissionSummaryFieldsFragment,
) =>
  sub.form.slug?.values
    ? toSubdomain(
        undefined,
        `/${sub.form.slug.values.value}/${extractId(sub.uri)}`,
      )
    : undefined;

export const useDeleteSubmission = () =>
  useMutation(mutationDocuments.deleteSubmission, {
    optimisticResponse: optimisticOperation("deleteSubmission"),
    update: updateAfterDelete("deleteSubmission", "Submission"),
  });

type User = {
  email: string;
  firstName: string;
  lastName: string;
  userUri: string;
};

const buildTracker = ({
  user,
  submissionUri,
}: {
  user?: User;
  submissionUri: string;
}) => ({
  __typename: "Tracker" as const,
  uri: TrackerNS.generateUri(),
  operationId: v4(),
  submissionUri,
  method: null,
  sentAt: new Date().toISOString(),
  action: "saved-to-matter",
  userUri: user?.userUri || UserNS.generateUri(),
  user: {
    // Generate new uri so it won't affect the original user entity
    uri: UserNS.generateUri(),
    __typename: "User" as const,
    email: user?.email || "",
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    initials: "",
    picture: "",
    latestLoginAt: "",
  },
});

export const useUpdateSubmission = (options?: { user?: User }) =>
  useMutation(mutationDocuments.updateSubmission, {
    update: (cache, { data }) => {
      if (data?.updateSubmission) {
        cache.updateQuery(
          {
            query: queryDocuments.submissions,
          },
          (ex) => {
            const exists = ex?.submissions.find(
              (s) => s.uri === data.updateSubmission.uri,
            );

            if (exists)
              return data.updateSubmission.values.syncStatus === "synced"
                ? {
                    ...ex!,
                    submissions: ex!.submissions.map((s) => {
                      if (s.uri !== data.updateSubmission.uri) return s;
                      return {
                        ...exists,
                        trackers: [
                          buildTracker({
                            user: options?.user,
                            submissionUri: exists.uri,
                          }),
                          ...(exists.trackers || []),
                        ],
                      };
                    }),
                  }
                : ex;

            return {
              submissions: [data.updateSubmission, ...(ex?.submissions || [])],
              __typename: "Query" as const,
            };
          },
        );
      }
    },
  });

export const useSendSubmission = () =>
  useMutation(mutationDocuments.sendSubmission, {
    update: (cache, { data }, { variables, context }) => {
      if (!data?.sendSubmission || !variables) return;

      const { uri } = variables;
      const sentValues = data.sendSubmission.values;
      const isReminder =
        sentValues.status === "active" || !!sentValues.sharedAt;
      const stopOnManualSend = !!context?.stopOnManualSend;
      const triggersStop =
        isReminder &&
        stopOnManualSend &&
        !sentValues.reminder?.manualStopTriggered;

      cache.modify({
        id: cache.identify({ __typename: "Submission", uri }),
        fields: {
          values: (existing) => {
            const reminder = existing?.reminder ?? sentValues.reminder;
            return {
              ...existing,
              ...sentValues,
              status: "active",
              ...(isReminder
                ? {
                    reminder: {
                      ...reminder,
                      __typename: "SubmissionReminder",
                      manualCount: (reminder?.manualCount ?? 0) + 1,
                      ...(triggersStop ? { manualStopTriggered: true } : {}),
                    },
                  }
                : {}),
            };
          },
          nextAutomaticReminderDate: (current) =>
            triggersStop ? null : current,
        },
      });
    },
  });

export const useSyncSubmission = () =>
  useMutation(mutationDocuments.syncSubmission);

export const useCopySubmissionLink = () =>
  useMutation(mutationDocuments.copySubmissionLink);

export const useVerifySubmission = () =>
  useMutation(mutationDocuments.verifySubmission);

export const useDownloadSubmissions = () => {
  const { terms } = loadLocale();
  const apollo = useApolloClient();

  return async (submissions: Gql.SubmissionSummaryFieldsFragment[]) => {
    const uniqueForms = Object.values(
      submissions.reduce<
        Record<string, Gql.SubmissionSummaryFieldsFragment["form"]>
      >(
        (aggr, s) =>
          aggr[s.form.uri] ? aggr : { ...aggr, [s.form.uri]: s.form },
        {},
      ),
    ).sort((a, b) => a.values.title.localeCompare(b.values.title));

    const getCSVDataForForm = async (
      form: Gql.SubmissionSummaryFieldsFragment["form"],
    ) => {
      try {
        const { data: sections } = await apollo.query({
          query: queryDocuments.sections,
          variables: { formUri: form.uri },
        });
        const { data: fields } = await apollo.query({
          query: queryDocuments.fields,
          variables: { formUri: form.uri },
        });

        const submissionsData = (
          await Promise.all(
            submissions.map(async (s) => {
              const { data } = await apollo.query({
                query: queryDocuments.submission,
                variables: { uri: s.uri },
              });
              return data?.submission;
            }),
          )
        ).filter(isNotNullOrUndefined);

        return generateCSVData({
          submissions: submissionsData.filter(
            (s) => s && s.form.uri === form.uri,
          ),
          formTitle: form.values.title,
          sections: sections?.sections,
          fields: fields?.fields,
        });
      } catch (error) {
        throw new Error(
          `Failed to generate CSV data for form "${form.values.uri}": ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    };

    const csvData = await Promise.all(uniqueForms.map(getCSVDataForForm));

    downloadAsCSV(
      csvData.join("\n\n"),
      `Web ${terms.enquiry.plural} - ${format(new Date(), "yyyy-MM-dd")}`,
    );
  };
};
